import { useCallback, useMemo, useState } from 'react'
import { calculateResults } from '../utils/roadmapCalcs'
import type {
  Applicant,
  Debts,
  RoadmapAnswers,
  RoadmapResults,
} from '../types/roadmap'

const EMPTY_APPLICANT: Applicant = {
  employmentType: null,
  grossAnnualIncome: 0,
  extraIncome: 0,
  rentalOtherIncome: 0,
}

export const INITIAL_ANSWERS: RoadmapAnswers = {
  goal: null,
  brokerStatus: null,
  brokerTarget: 0,

  propertyPrice: 0,
  timeframe: null,
  buyingWith: null,

  deposit: 0,
  isFirstHomeBuyer: null,
  purchasingCosts: 0,
  purchasingCostsEstimated: false,
  cashBuffer: 0,
  cashBufferEstimated: false,
  investorReserve: 0,
  otherCosts: 0,

  savedSoFar: 0,
  giftAmount: 0,
  otherConfirmedFunds: 0,
  keepUntouched: 0,

  applicantOne: {
    ...EMPTY_APPLICANT,
  },
  applicantTwo: {
    ...EMPTY_APPLICANT,
  },

  dependants: 0,
  debts: {
    homeLoans: 0,
    carPersonalLoans: 0,
    creditCardLimits: 0,
    hecs: 0,
    buyNowPayLater: 0,
  },
  currentRent: 0,
  livingExpenses: 0,

  payFrequency: null,
  savingNowPerPay: 0,
  couldSavePerPay: 0,

  name: '',
  checkedActions: [],
}

interface UseRoadmap {
  answers: RoadmapAnswers
  results: RoadmapResults
  set: <K extends keyof RoadmapAnswers>(
    key: K,
    value: RoadmapAnswers[K],
  ) => void
  setApplicant: (
    which: 'applicantOne' | 'applicantTwo',
    patch: Partial<Applicant>,
  ) => void
  setDebts: (patch: Partial<Debts>) => void
  toggleAction: (id: string) => void
  reset: () => void
}

/**
 * Every answer lives here, in memory only. Nothing is written to browser
 * storage and nothing is sent anywhere — the direct consequence being that a
 * refresh starts over, which is the right trade for a finance tool.
 */
export function useRoadmap(): UseRoadmap {
  const [answers, setAnswers] = useState<RoadmapAnswers>(INITIAL_ANSWERS)

  const set = useCallback(
    <K extends keyof RoadmapAnswers>(key: K, value: RoadmapAnswers[K]) => {
      setAnswers((prev) => ({
        ...prev,
        [key]: value,
      }))
    },
    [],
  )

  const setApplicant = useCallback(
    (which: 'applicantOne' | 'applicantTwo', patch: Partial<Applicant>) => {
      setAnswers((prev) => ({
        ...prev,
        [which]: {
          ...prev[which],
          ...patch,
        },
      }))
    },
    [],
  )

  const setDebts = useCallback((patch: Partial<Debts>) => {
    setAnswers((prev) => ({
      ...prev,
      debts: {
        ...prev.debts,
        ...patch,
      },
    }))
  }, [])

  const toggleAction = useCallback((id: string) => {
    setAnswers((prev) => ({
      ...prev,
      checkedActions: prev.checkedActions.includes(id)
        ? prev.checkedActions.filter((item) => item !== id)
        : [...prev.checkedActions, id],
    }))
  }, [])

  const reset = useCallback(() => setAnswers(INITIAL_ANSWERS), [])

  const results = useMemo(() => calculateResults(answers), [answers])

  return {
    answers,
    results,
    set,
    setApplicant,
    setDebts,
    toggleAction,
    reset,
  }
}
